/**
 * `/help/$guideSlug` — an in-app help guide (AUTONOMY · Layer 3).
 *
 * Renders the guide's sections with anchors (`?section=`) for the
 * contextual help ("?" on each screen) and a side index. The content is
 * i18n-ized TSX from the registry (`features/help`).
 */
import { createEffect, For, on, Show } from "solid-js";
import { createRoute, useParams, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";

import { appRoute } from "./route";
import { PageContainer, PageHeader } from "@fleet/ui";
import { NotFoundState } from "@/components/ui/not-found-state";
import { useI18n } from "@/providers/i18n-provider";
import { findGuide } from "@/features/help/registry";

function sectionDomId(id: string): string {
  return `help-section-${id}`;
}

function HelpGuidePage() {
  const params = useParams({ from: "/_app/help/$guideSlug" });
  const search = useSearch({ from: "/_app/help/$guideSlug" });
  const { t } = useI18n();

  const guide = () => findGuide(params().guideSlug);

  // Contextual help: on arrival with `?section=`, scroll to the section.
  createEffect(
    on(
      () => [params().guideSlug, search().section] as const,
      ([, section]) => {
        if (!section) {
          return;
        }
        // The scroll is deferred a frame so the sections are already mounted.
        requestAnimationFrame(() => {
          document.getElementById(sectionDomId(section))?.scrollIntoView({ block: "start" });
        });
      },
    ),
  );

  return (
    <Show when={guide()} fallback={<NotFoundState title={t(msg`Guide not found`)} />}>
      {(g) => (
        <PageContainer maxWidth="5xl">
          <div class="space-y-6 p-4 md:p-6">
            <PageHeader
              backTo="/help"
              icon={g().icon}
              title={t(g().title)}
              description={t(g().audience)}
            />
            <p class="max-w-3xl text-sm leading-relaxed text-muted-foreground">
              {t(g().description)}
            </p>

            <div class="flex flex-col gap-8 lg:flex-row">
              {/* Section index */}
              <nav
                class="top-4 h-fit shrink-0 space-y-1 lg:sticky lg:w-60"
                aria-label={t(msg`Guide sections`)}
              >
                <For each={g().sections}>
                  {(section) => (
                    <a
                      href={`#${sectionDomId(section.id)}`}
                      class="block rounded-xl px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
                      data-testid={`help-toc-${section.id}`}
                    >
                      {t(section.title)}
                    </a>
                  )}
                </For>
              </nav>

              {/* Contenido */}
              <div class="min-w-0 flex-1 space-y-8">
                <For each={g().sections}>
                  {(section) => (
                    <section
                      id={sectionDomId(section.id)}
                      class="scroll-mt-4 space-y-3"
                      data-testid={`help-section-${section.id}`}
                    >
                      <h2 class="text-lg font-bold text-foreground">{t(section.title)}</h2>
                      <p class="text-sm font-medium leading-relaxed text-foreground/80">
                        {t(section.summary)}
                      </p>
                      <div class="flex flex-col gap-3">
                        <section.body />
                      </div>
                    </section>
                  )}
                </For>
              </div>
            </div>
          </div>
        </PageContainer>
      )}
    </Show>
  );
}

export const helpGuideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/help/$guideSlug",
  validateSearch: (search: Record<string, unknown>): { section?: string } => {
    const section = typeof search.section === "string" ? search.section : undefined;
    return section ? { section } : {};
  },
  component: HelpGuidePage,
});
